// =====================================================================
// JOIN DISTRIBUTOR — /distribuidor
// Form público para que electricistas, integradores, arquitectos,
// distribuidores etc. soliciten acceso al programa de distribuidores
// NULED + Lutron.
//
// Submit → edge function submit-distributor-application
//   → INSERT en distributor_applications
//   → email a elias@nuled.mx + ventas@nuled.mx (vía Resend)
// Post-submit → confirmación + botón "Continuar por WhatsApp"
// =====================================================================

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { theme } from '../styles/theme';
import { Seo } from '../lib/seo';
import { supabase } from '../lib/supabase';
import { getAttribution, getTrackingSessionId, trackEvent } from '../lib/tracking';

const WHATSAPP_PHONE = '525549332582';

type CustomerType =
  | 'distribuidor' | 'electricista' | 'integrador'
  | 'arquitecto' | 'disenador' | 'contratista'
  | 'tienda_online' | 'otro';

type Volume =
  | 'menos_50k' | '50k_150k' | '150k_500k'
  | '500k_1m' | 'mas_1m' | 'no_se' | '';

type Acquisition =
  | 'google' | 'instagram' | 'referido'
  | 'feria' | 'distribuidor_existente' | 'otro' | '';

type ProductLine =
  | 'nuled_led' | 'lutron_caseta' | 'lutron_radiora3'
  | 'lutron_maestro' | 'lutron_wallbox' | 'lutron_vive'
  | 'lutron_homeworks';

type FormState = {
  contact_name: string;
  business_name: string;
  rfc: string;
  email: string;
  phone: string;
  whatsapp: string;
  city: string;
  state: string;
  customer_type: CustomerType | '';
  product_lines: ProductLine[];
  monthly_volume_mxn: Volume;
  acquisition_channel: Acquisition;
  message: string;
};

const EMPTY: FormState = {
  contact_name: '',
  business_name: '',
  rfc: '',
  email: '',
  phone: '',
  whatsapp: '',
  city: '',
  state: '',
  customer_type: '',
  product_lines: [],
  monthly_volume_mxn: '',
  acquisition_channel: '',
  message: '',
};

const CUSTOMER_TYPES: { value: CustomerType; label: string; hint: string }[] = [
  { value: 'distribuidor',   label: 'Distribuidor',          hint: 'Compro para revender en mi propia tienda o catálogo' },
  { value: 'electricista',   label: 'Electricista',          hint: 'Compro para instalar en obras de mis clientes' },
  { value: 'integrador',     label: 'Integrador / Instalador', hint: 'Diseño e instalo sistemas de iluminación y control' },
  { value: 'arquitecto',     label: 'Arquitecto',            hint: 'Especifico iluminación en mis proyectos' },
  { value: 'disenador',      label: 'Diseñador / LD',        hint: 'Lighting designer o diseñador de interiores' },
  { value: 'contratista',    label: 'Contratista',           hint: 'Constructora o contratista general' },
  { value: 'tienda_online',  label: 'Tienda online',         hint: 'Vendo iluminación por e-commerce o marketplace' },
  { value: 'otro',           label: 'Otro',                  hint: 'Cuéntanos en el mensaje al final' },
];

const PRODUCT_LINES: { value: ProductLine; label: string; group: 'NULED' | 'Lutron' }[] = [
  { value: 'nuled_led',         label: 'NULED — Luminarias LED',   group: 'NULED' },
  { value: 'lutron_caseta',     label: 'Lutron Caséta',            group: 'Lutron' },
  { value: 'lutron_radiora3',   label: 'Lutron RadioRA 3',         group: 'Lutron' },
  { value: 'lutron_maestro',    label: 'Lutron Maestro',           group: 'Lutron' },
  { value: 'lutron_wallbox',    label: 'Lutron Wallbox',           group: 'Lutron' },
  { value: 'lutron_vive',       label: 'Lutron Vive',              group: 'Lutron' },
  { value: 'lutron_homeworks',  label: 'Lutron HomeWorks',         group: 'Lutron' },
];

const VOLUMES: { value: Exclude<Volume, ''>; label: string }[] = [
  { value: 'menos_50k',  label: 'Menos de $50,000 MXN/mes' },
  { value: '50k_150k',   label: '$50,000 – $150,000 MXN/mes' },
  { value: '150k_500k',  label: '$150,000 – $500,000 MXN/mes' },
  { value: '500k_1m',    label: '$500,000 – $1,000,000 MXN/mes' },
  { value: 'mas_1m',     label: 'Más de $1,000,000 MXN/mes' },
  { value: 'no_se',      label: 'No estoy seguro todavía' },
];

const ACQUISITION: { value: Exclude<Acquisition, ''>; label: string }[] = [
  { value: 'google',                 label: 'Google' },
  { value: 'instagram',              label: 'Instagram / Facebook' },
  { value: 'referido',               label: 'Me lo recomendaron' },
  { value: 'feria',                  label: 'Feria o evento' },
  { value: 'distribuidor_existente', label: 'Soy cliente de NULED' },
  { value: 'otro',                   label: 'Otro' },
];

export function JoinDistributor() {
  const [params] = useSearchParams();
  const [f, setF] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState<{ id: string; deal_id: string | null } | null>(null);

  // Leer los parámetros también captura y conserva el último origen no-directo por 90 días.
  const attribution = useMemo(() => getAttribution(), [params]);

  // Cuando llegamos a la pantalla de confirmación, saltar al top INSTANT —
  // un smooth scroll se cancela al cambiar el layout (form largo → confirmación corta)
  // y dejaba al usuario viendo el body en blanco debajo del footer.
  useEffect(() => {
    if (sent) window.scrollTo(0, 0);
  }, [sent]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setF(prev => ({ ...prev, [k]: v }));

  const toggleLine = (line: ProductLine) => {
    setF(prev => ({
      ...prev,
      product_lines: prev.product_lines.includes(line)
        ? prev.product_lines.filter(l => l !== line)
        : [...prev.product_lines, line],
    }));
  };

  const localValidate = (): string | null => {
    if (f.contact_name.trim().length < 2) return 'Escribe tu nombre.';
    if (f.business_name.trim().length < 2) return 'Escribe el nombre de tu empresa o negocio.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) return 'El email no es válido.';
    if (f.phone.replace(/\D/g, '').length < 8) return 'El teléfono no es válido.';
    if (!f.customer_type) return 'Selecciona qué tipo de cliente eres.';
    if (f.product_lines.length === 0) return 'Selecciona al menos una línea de producto.';
    return null;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    const v = localValidate();
    if (v) { setErr(v); return; }

    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('submit-distributor-application', {
        body: {
          contact_name: f.contact_name,
          business_name: f.business_name,
          rfc: f.rfc || null,
          email: f.email,
          phone: f.phone,
          whatsapp: f.whatsapp || null,
          city: f.city || null,
          state: f.state || null,
          customer_type: f.customer_type,
          product_lines: f.product_lines,
          monthly_volume_mxn: f.monthly_volume_mxn || null,
          acquisition_channel: f.acquisition_channel || null,
          message: f.message || null,
          ...attribution,
          session_id: getTrackingSessionId(),
        },
      });
      if (error) {
        setErr(error.message || 'No pudimos enviar tu solicitud. Intenta de nuevo.');
        return;
      }
      const r = data as { ok?: boolean; id?: string; deal_id?: string | null; error?: string } | null;
      if (!r?.ok || !r.id) {
        setErr(r?.error || 'No pudimos enviar tu solicitud. Intenta de nuevo.');
        return;
      }
      setSent({ id: r.id, deal_id: r.deal_id ?? null });
      // La función ya persistió el evento de forma confiable; aquí solo se envía a Google/Meta.
      void trackEvent('lead_distribuidor', {
        application_id: r.id,
        customer_type: f.customer_type,
        product_lines: f.product_lines,
        monthly_volume_mxn: f.monthly_volume_mxn,
        acquisition_channel: f.acquisition_channel,
      }, undefined, { persist: false });
    } catch (e) {
      setErr((e as Error).message || 'Error inesperado.');
    } finally {
      setSubmitting(false);
    }
  };

  // ============= PANTALLA DE CONFIRMACIÓN =============
  if (sent) {
    const typeLabel = CUSTOMER_TYPES.find(t => t.value === f.customer_type)?.label || '';
    const linesLabel = PRODUCT_LINES
      .filter(l => f.product_lines.includes(l.value))
      .map(l => l.label).join(', ');
    const waMessage = `Hola NULED, acabo de enviar mi solicitud para ser distribuidor.\n\n` +
      `Nombre: ${f.contact_name}\nEmpresa: ${f.business_name}\n` +
      `Tipo: ${typeLabel}\nLíneas de interés: ${linesLabel}\n\n` +
      `Mi folio es ${sent.id.slice(0, 8)}.`;
    const waHref = `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(waMessage)}`;

    return (
      <div style={{ background: theme.bg, color: theme.text, fontFamily: theme.font.sans, minHeight: '100vh' }}>
        <Seo title="Solicitud enviada · NULED" description="Gracias por solicitar acceso al programa de distribuidores NULED." />
        <Header />
        <section style={{ maxWidth: 640, margin: '0 auto', padding: '60px 24px 80px' }}>
          <div style={{
            fontSize: 11, letterSpacing: 2, textTransform: 'uppercase',
            color: theme.accentDark, fontWeight: 700,
          }}>
            Solicitud recibida
          </div>
          <h1 style={{
            fontFamily: theme.font.head, fontSize: 32, fontWeight: 400,
            color: theme.brand, margin: '8px 0 16px',
          }}>
            Gracias, {f.contact_name.split(' ')[0]}.
          </h1>
          <p style={{ color: theme.textMuted, fontSize: 15, lineHeight: 1.6, margin: '0 0 8px' }}>
            Recibimos tu solicitud para <strong style={{ color: theme.brand }}>{f.business_name}</strong>.
            Elias y el equipo comercial te contactan en menos de 24 horas hábiles.
          </p>
          <p style={{ color: theme.textMuted, fontSize: 13, margin: '0 0 32px' }}>
            Folio: <code style={{ color: theme.brand }}>{sent.id.slice(0, 8).toUpperCase()}</code>
          </p>

          <div style={{
            background: theme.accentSoft, border: `1px solid ${theme.accent}`,
            padding: 20, marginBottom: 32,
          }}>
            <div style={{ fontWeight: 700, color: theme.brand, marginBottom: 6, fontSize: 14 }}>
              ¿Quieres avanzar ahora?
            </div>
            <p style={{ color: theme.brand, fontSize: 13, lineHeight: 1.5, margin: '0 0 14px' }}>
              Continúa por WhatsApp con tu solicitud pre-cargada. Te asignamos
              al agente de ventas en el momento.
            </p>
            <a
              href={waHref}
              target="_blank"
              rel="noreferrer"
              onClick={() => void trackEvent('click_whatsapp', {
                source: 'distributor_confirmation',
                application_id: sent.id,
              }, undefined, {
                distributorApplicationId: sent.id,
                dealId: sent.deal_id,
              })}
              style={{
              display: 'inline-block', background: '#25D366', color: '#FFFFFF',
              padding: '14px 24px', fontSize: 14, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: 0.4,
              textDecoration: 'none', fontFamily: theme.font.sans,
              }}>
              Continuar por WhatsApp →
            </a>
          </div>

          <a href="/" style={{ color: theme.accentDark, fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
            ← Volver al inicio
          </a>
        </section>
        <Footer />
      </div>
    );
  }

  // ============= FORMULARIO =============
  return (
    <div style={{ background: theme.bg, color: theme.text, fontFamily: theme.font.sans, minHeight: '100vh' }}>
      <Seo
        title="Vuélvete distribuidor · NULED"
        description="Solicita acceso al programa de distribuidores NULED + Lutron. Precios de mayorista, capacitación técnica y soporte comercial."
      />
      <Header />

      <section style={{ maxWidth: 760, margin: '0 auto', padding: '40px 24px 80px' }}>
        {/* HERO */}
        <div style={{
          fontSize: 11, letterSpacing: 2, textTransform: 'uppercase',
          color: theme.accentDark, fontWeight: 700,
        }}>
          Programa NULED + Lutron
        </div>
        <h1 style={{
          fontFamily: theme.font.head, fontSize: 36, fontWeight: 400,
          color: theme.brand, margin: '8px 0 12px', lineHeight: 1.15,
        }}>
          Vuélvete distribuidor
        </h1>
        <p style={{ color: theme.textMuted, fontSize: 15, lineHeight: 1.6, margin: 0 }}>
          Acceso a precios de mayorista en luminarias NULED y la línea
          completa de controles Lutron. Capacitación técnica, soporte de
          proyecto y acompañamiento comercial. Cuéntanos un poco de ti y
          te contactamos en menos de 24 horas.
        </p>

        {/* BENEFITS */}
        <div style={{
          marginTop: 28, marginBottom: 36,
          display: 'grid', gap: 12,
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        }}>
          {[
            { t: 'Precios por nivel', d: 'Descuentos escalonados según volumen y especialización.' },
            { t: 'Mix NULED + Lutron', d: 'Una sola orden, un solo crédito, una sola facturación.' },
            { t: 'Capacitación', d: 'Educador Lutron oficial — cursos presenciales y online.' },
          ].map(b => (
            <div key={b.t} style={{ border: `1px solid ${theme.border}`, padding: 16, background: theme.bgSoft }}>
              <div style={{
                fontSize: 11, color: theme.accentDark, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4,
              }}>
                {b.t}
              </div>
              <div style={{ fontSize: 13, color: theme.text, lineHeight: 1.5 }}>{b.d}</div>
            </div>
          ))}
        </div>

        {/* FORM */}
        <form onSubmit={submit} style={{
          background: theme.bg, border: `1px solid ${theme.border}`, padding: 28,
        }}>
          <SectionTitle>Datos de contacto</SectionTitle>
          <Grid>
            <Field label="Nombre completo *">
              <input style={inputStyle} value={f.contact_name}
                onChange={e => set('contact_name', e.target.value)} autoComplete="name" required />
            </Field>
            <Field label="Empresa o negocio *">
              <input style={inputStyle} value={f.business_name}
                onChange={e => set('business_name', e.target.value)} autoComplete="organization" required />
            </Field>
            <Field label="Email *">
              <input type="email" style={inputStyle} value={f.email}
                onChange={e => set('email', e.target.value)} autoComplete="email" required />
            </Field>
            <Field label="Teléfono *">
              <input type="tel" style={inputStyle} value={f.phone}
                onChange={e => set('phone', e.target.value)} autoComplete="tel"
                placeholder="55 1234 5678" required />
            </Field>
            <Field label="WhatsApp (si es distinto)">
              <input type="tel" style={inputStyle} value={f.whatsapp}
                onChange={e => set('whatsapp', e.target.value)} placeholder="55 1234 5678" />
            </Field>
            <Field label="RFC (opcional)">
              <input style={inputStyle} value={f.rfc}
                onChange={e => set('rfc', e.target.value.toUpperCase())} placeholder="ABCD123456EF7" />
            </Field>
            <Field label="Ciudad">
              <input style={inputStyle} value={f.city}
                onChange={e => set('city', e.target.value)} autoComplete="address-level2" />
            </Field>
            <Field label="Estado">
              <input style={inputStyle} value={f.state}
                onChange={e => set('state', e.target.value)} autoComplete="address-level1" />
            </Field>
          </Grid>

          <Hr />

          <SectionTitle>¿Qué tipo de cliente eres? *</SectionTitle>
          <div style={{
            display: 'grid', gap: 8,
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            marginBottom: 4,
          }}>
            {CUSTOMER_TYPES.map(t => (
              <RadioCard
                key={t.value}
                checked={f.customer_type === t.value}
                onClick={() => set('customer_type', t.value)}
                title={t.label}
                hint={t.hint}
              />
            ))}
          </div>

          <Hr />

          <SectionTitle>¿Qué líneas te interesan? * (puedes elegir varias)</SectionTitle>
          <div style={{
            fontSize: 11, color: theme.textMuted, marginBottom: 12,
            textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700,
          }}>
            NULED
          </div>
          <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
            {PRODUCT_LINES.filter(l => l.group === 'NULED').map(l => (
              <CheckCard
                key={l.value}
                checked={f.product_lines.includes(l.value)}
                onClick={() => toggleLine(l.value)}
                title={l.label}
              />
            ))}
          </div>
          <div style={{
            fontSize: 11, color: theme.textMuted, marginBottom: 12,
            textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700,
          }}>
            Lutron
          </div>
          <div style={{
            display: 'grid', gap: 8,
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          }}>
            {PRODUCT_LINES.filter(l => l.group === 'Lutron').map(l => (
              <CheckCard
                key={l.value}
                checked={f.product_lines.includes(l.value)}
                onClick={() => toggleLine(l.value)}
                title={l.label}
              />
            ))}
          </div>

          <Hr />

          <SectionTitle>Volumen mensual estimado</SectionTitle>
          <Field label="">
            <select style={selectStyle} value={f.monthly_volume_mxn}
              onChange={e => set('monthly_volume_mxn', e.target.value as Volume)}>
              <option value="">— Selecciona un rango —</option>
              {VOLUMES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
            </select>
          </Field>

          <div style={{ marginTop: 16 }}>
            <Field label="¿Cómo nos conociste?">
              <select style={selectStyle} value={f.acquisition_channel}
                onChange={e => set('acquisition_channel', e.target.value as Acquisition)}>
                <option value="">— Opcional —</option>
                {ACQUISITION.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </Field>
          </div>

          <Hr />

          <SectionTitle>¿Algo más que quieras contarnos?</SectionTitle>
          <Field label="">
            <textarea style={{ ...inputStyle, minHeight: 100, resize: 'vertical', fontFamily: theme.font.sans }}
              value={f.message} onChange={e => set('message', e.target.value)}
              placeholder="Proyecto específico, marcas que manejas hoy, fechas importantes, etc." />
          </Field>

          {err && (
            <div style={{
              color: theme.err, fontSize: 13, marginTop: 16, padding: 12,
              background: 'rgba(220,38,38,0.05)', border: `1px solid ${theme.err}`,
            }}>
              {err}
            </div>
          )}

          <button type="submit" disabled={submitting} style={{
            marginTop: 24, width: '100%', background: theme.accent, color: theme.brand,
            border: 'none', padding: '16px 24px', fontSize: 14, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 0.6,
            cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.7 : 1,
            fontFamily: theme.font.sans,
          }}>
            {submitting ? 'Enviando…' : 'Enviar solicitud'}
          </button>

          <p style={{ color: theme.textMuted, fontSize: 11, lineHeight: 1.5, marginTop: 14, marginBottom: 0 }}>
            Al enviar aceptas que NULED te contacte por email, WhatsApp o teléfono
            para evaluar tu solicitud. No compartimos tus datos con terceros.
          </p>
        </form>
      </section>
      <Footer />
    </div>
  );
}

// ============= COMPONENTES =============

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 style={{
      fontFamily: theme.font.head, fontSize: 16, fontWeight: 700,
      color: theme.brand, margin: '0 0 16px', textTransform: 'uppercase',
      letterSpacing: 0.6,
    }}>
      {children}
    </h2>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display: 'grid', gap: 14,
      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    }}>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      {label && (
        <div style={{
          fontSize: 11, color: theme.textMuted, marginBottom: 6,
          textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700,
        }}>
          {label}
        </div>
      )}
      {children}
    </label>
  );
}

function Hr() {
  return <div style={{ height: 1, background: theme.border, margin: '28px 0' }} />;
}

function RadioCard({ checked, onClick, title, hint }: {
  checked: boolean; onClick: () => void; title: string; hint: string;
}) {
  return (
    <button type="button" onClick={onClick} style={{
      textAlign: 'left', background: checked ? theme.accentSoft : theme.bg,
      border: `1px solid ${checked ? theme.accent : theme.border}`,
      padding: '12px 14px', cursor: 'pointer', fontFamily: theme.font.sans,
      display: 'block', width: '100%',
    }}>
      <div style={{
        fontSize: 14, fontWeight: 700, color: theme.brand, marginBottom: 2,
      }}>
        {title}
      </div>
      <div style={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.4 }}>
        {hint}
      </div>
    </button>
  );
}

function CheckCard({ checked, onClick, title }: {
  checked: boolean; onClick: () => void; title: string;
}) {
  return (
    <button type="button" onClick={onClick} style={{
      textAlign: 'left', background: checked ? theme.accentSoft : theme.bg,
      border: `1px solid ${checked ? theme.accent : theme.border}`,
      padding: '10px 14px', cursor: 'pointer', fontFamily: theme.font.sans,
      display: 'flex', alignItems: 'center', gap: 10, width: '100%',
    }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 18, height: 18,
        border: `2px solid ${checked ? theme.brand : theme.borderStrong}`,
        background: checked ? theme.brand : 'transparent', flexShrink: 0,
      }}>
        {checked && (
          <svg width="10" height="10" viewBox="0 0 10 10">
            <path d="M1 5 L4 8 L9 2" stroke="#FFFFFF" strokeWidth="2" fill="none" />
          </svg>
        )}
      </span>
      <span style={{ fontSize: 14, color: theme.brand, fontWeight: 500 }}>
        {title}
      </span>
    </button>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', background: theme.bg, border: `1px solid ${theme.border}`,
  padding: '12px 14px', color: theme.text, fontSize: 14,
  fontFamily: theme.font.sans, outline: 'none', boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  appearance: 'none',
  backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path d='M1 1 L6 6 L11 1' stroke='%232D484F' stroke-width='1.5' fill='none'/></svg>")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 14px center',
  paddingRight: 36,
  cursor: 'pointer',
};
